import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Papa from "papaparse";
import { checkListExists } from "src/services/EngagementAPIs";
import { AxiosError } from "axios";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import { Trash2, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ColumnData {
  column: string;
  type: string;
  sample: string;
  dataType: string;
}

const CreateListScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [inputTag, setInputTag] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<ColumnData[]>([]);
  const [memberCount, setMemberCount] = useState<number>(0);

  const handleTagInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputTag.trim()) {
      setTags([...tags, inputTag.trim()]);
      setInputTag("");
    }
  };

  const parseCSV = (fileToParse: File) => {
    Papa.parse(fileToParse, {
      complete: results => {
        if (results.data.length > 0) {
          const headers = results.data[0] as string[];
          const sampleRows = results.data.slice(1, 3) as string[][];

          const count = results.data.length - 1;
          setMemberCount(count);

          const columnsData: ColumnData[] = headers.map((header, index) => {
            const samples = sampleRows.map(row => row[index] || "").join(", ");
            const dataType =
              typeof sampleRows[0]?.[index] === "number" ? "Number" : "Text";

            return {
              column: header,
              type: header.toLowerCase(),
              sample: samples,
              dataType,
            };
          });

          setCsvData(columnsData);
        }
      },
      header: false,
      skipEmptyLines: true,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    const isCSV =
      selectedFile.type === "text/csv" ||
      selectedFile.name.toLowerCase().endsWith(".csv");

    if (!isCSV) {
      message.warning(t("audienceOverview.createList.onlyCsvAllowed"));
      e.target.value = "";
      setFile(null);
      setMemberCount(0);
      return;
    }

    setFile(selectedFile);
    parseCSV(selectedFile);
  };

  const handleCreateList = async () => {
    if (!name.trim()) {
      message.error(t("audienceOverview.createList.enterListNameError"));
      return;
    }

    if (!file) {
      message.error(t("audienceOverview.createList.uploadCsvError"));
      return;
    }

    if (csvData.length === 0) {
      message.error(t("audienceOverview.createList.noDataInCsvError"));
      return;
    }

    try {
      const tenantId = parseInt(localStorage.getItem("tenant_id") || "0", 10);

      await checkListExists({ tenant_id: tenantId, list_name: name });

      navigate("/account/engagement/import-columns", {
        state: {
          listName: name,
          tags,
          csvFile: file,
          columnsData: csvData,
          memberCount,
        },
      });
    } catch (err) {
      const axiosErr = err as AxiosError<{ detail: string }>;
      const detail =
        axiosErr?.response?.data?.detail ||
        t("audienceOverview.createList.listNameExists");
      message.error(detail);
    }
  };

  return (
    <div className="create-list-container">
      <button
        className="button button--primary create-list-back-button"
        onClick={() => navigate("/account/engagement/select-list-or-segment")}
      >
        {t("audienceOverview.createList.back")}
      </button>

      <h1 className="create-list-title">{t("audienceOverview.createList.title")}</h1>
      <p className="create-list-subtitle">
        {t("audienceOverview.createList.subtitlePrefix")}{" "}
        <strong>{t("audienceOverview.createList.requiredFirstName")}</strong>{" "}
        {t("audienceOverview.createList.subtitleAnd")}{" "}
        <strong>{t("audienceOverview.createList.requiredEmail")}</strong>
        .
      </p>

      <div className="form-container create-list-form">
        <label className="form-label">{t("audienceOverview.createList.listNameLabel")}</label>
        <StyledTextInput
          placeholder={t("audienceOverview.createList.listNamePlaceholder")}
          value={name}
          onChange={e => setName(e.target.value)}
          className="create-list-input"
        />

        <label className="form-label">{t("audienceOverview.createList.tagsLabel")}</label>
        <StyledTextInput
          placeholder={t("audienceOverview.createList.tagsPlaceholder")}
          value={inputTag}
          onChange={e => setInputTag(e.target.value)}
          onKeyDown={handleTagInput}
          className="create-list-input"
        />

        <div className="tags-list">
          {tags.map((tag, index) => (
            <span key={index} className="tag create-list-tag">
              {tag}
              <button
                type="button"
                className="tag-remove-btn"
                onClick={() =>
                  setTags(prev => prev.filter((_, chipIndex) => chipIndex !== index))
                }
              >
                x
              </button>
            </span>
          ))}
        </div>

        <label className="upload-area create-list-upload-area">
          <input type="file" accept=".csv" onChange={handleFileChange} hidden />
          <div className="upload-box create-list-upload-box">
            <div className="upload-icon">
              <img
                src="/Icons/upload-arrow.svg"
                alt={t("audienceOverview.createList.csvIconAlt")}
                className="csv-icon"
              />
            </div>
            <div className="upload-text">{t("audienceOverview.createList.uploadList")}</div>
            <div className="upload-subtext">
              {t("audienceOverview.createList.uploadSubtext")}
            </div>
          </div>
        </label>
        {!file && (
          <p className="create-list-upload-note">
            * {t("audienceOverview.createList.fileMustInclude")}{" "}
            {t("audienceOverview.createList.requiredFirstName")}
            {t("audienceOverview.createList.subtitleAnd")}{" "}
            {t("audienceOverview.createList.requiredEmail")}.
          </p>
        )}

        {file && (
          <div className="uploaded-file-row">
            <span className="uploaded-file-name">
              <Paperclip size={14} className="uploaded-file-paperclip" />
              {file.name}
            </span>
            <button
              type="button"
              className="uploaded-file-remove"
              onClick={() => {
                setFile(null);
                setCsvData([]);
                setMemberCount(0);
              }}
              aria-label={t("audienceOverview.createList.removeUploadedFileAria")}
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="action-buttons create-list-actions">
        <button
          className="button button--outline"
          onClick={() => navigate("/account/engagement/select-list-or-segment")}
        >
          {t("audienceOverview.createList.cancel")}
        </button>
        <button className="button button--primary" onClick={handleCreateList}>
          {t("audienceOverview.createList.createList")}
        </button>
      </div>
    </div>
  );
};

export default CreateListScreen;
